"use client";

import { useEffect, useRef } from "react";

import { authClient } from "@/lib/auth-client";
import {
  clearTermsAcceptanceIntent,
  getTermsAcceptanceIntent,
} from "@/lib/terms-intent";
import { api } from "@/lib/trpc/react";

/**
 * TermsAcceptanceProvider
 *
 * This provider processes terms acceptance after OAuth callback.
 * It checks for a terms acceptance intent cookie (set before OAuth)
 * and calls the acceptTerms mutation if the user is authenticated.
 *
 * Place this in layouts where authenticated users land after OAuth.
 */
export function TermsAcceptanceProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { data: session, isPending } = authClient.useSession();
  const hasProcessed = useRef(false);

  const acceptTermsMutation = api.userSettings.acceptTerms.useMutation({
    onSuccess: () => {
      console.log("✅ Terms acceptance recorded successfully");
      clearTermsAcceptanceIntent();
    },
    onError: (error) => {
      console.error("❌ Failed to record terms acceptance:", error);
      // Still clear the intent to avoid infinite loops
      clearTermsAcceptanceIntent();
    },
  });

  useEffect(() => {
    // Don't process if:
    // - Session is still loading
    // - No session (user not authenticated)
    // - Already processed in this render cycle
    // - Mutation is already in progress
    if (
      isPending ||
      !session?.user ||
      hasProcessed.current ||
      acceptTermsMutation.isPending
    ) {
      return;
    }

    // Check for terms acceptance intent
    const shouldAcceptTerms = getTermsAcceptanceIntent();

    if (shouldAcceptTerms) {
      console.log(
        "📝 Processing terms acceptance intent for user:",
        session.user.id,
      );
      hasProcessed.current = true;
      acceptTermsMutation.mutate();
    }
  }, [session, isPending, acceptTermsMutation]);

  return <>{children}</>;
}
